// Konzole správy (kap. 38): vlevo strom funkcí s hledáním, vpravo stránka = běžný pohled (seznam / záznam / vlastní)
// vložený do obsahu. Pás karet je ten vložené stránky – jen její příkazy. Strom jde do hloubky (zákazníci → uživatelé →
// uživatel); velké větve se načítají až po rozbalení. Otevření záznamu ze seznamu vybere jeho uzel ve stromu; záznam bez
// uzlu (nový) se ukáže jako detail přes celou šířku, detaily se vrství a drobečky / zavření vrací zpět.
// Zpět / vpřed v historii uzlů, „Nový ▾“ z příkazů konzole, „Otevřít v novém okně“ (i Shift + dvojklik),
// hledání ve stromu i na serveru (uživatelé, zákazníci), paměť rozbalení a poslední stránky.
import { action, ApiError } from '../api';
import type { ConsoleDescriptor, ConsoleNode, ConsoleSearchResult, WindowDescriptor, Mode } from '../contract';
import { h, debounce, asObject } from '../dom';
import { t, tk } from '../i18n';
import { icon } from '../icons';
import { IwSplit, IwTree, type TreeNode, loader, errorState, emptyState } from '../components/misc';
import { IwMenu, type MenuItem } from '../components/menu';
import { IwAddressBar } from '../components/addressbar';
import { headButton, IwCommandBar } from '../components/commandbar';
import { useBar } from '../components/chrome';
import { savePref } from '../theme';
import type { IwWindow, WindowView } from '../wm/window';
import type { WindowManager, OpenOptions } from '../wm/manager';
import type { ViewHost } from './factory';
import { createView } from './factory';

interface Slot {
  app: string;
  window: string;
  params: Record<string, unknown>;
  title: string;
  view: WindowView | null;
  el: HTMLElement;
  onResult?: (r: unknown) => void;
  /** příkazy okna (Nový… stránky do „Nový ▾“) */
  commands?: WindowDescriptor['commands'];
  /** detail přes celou šířku (ne stránka uzlu) */
  isDetail?: boolean;
  /** záhlaví, které si pohled nastavil (ovládání bar) – konzole ho ukáže v záhlaví okna, je-li pohled nahoře */
  header?: { start?: Node[]; main?: Node[] } | null;
}

/** Pod touto šířkou se strom schová a vysouvá se tlačítkem (jako na telefonu). */
const NARROW = 720;

interface Target {
  app: string;
  window: string;
  params: Record<string, unknown>;
  onResult?: (r: unknown) => void;
}

/** Uložené předvolby konzole: šířka stromu, rozbalené uzly, poslední stránka. */
interface Saved {
  tree?: number;
  expanded?: string[];
  last?: string;
}

export class ConsoleView implements WindowView {
  readonly el: HTMLElement;
  private split = new IwSplit();
  private tree = new IwTree();
  private search = h('input', { type: 'search', class: 'input console-search', placeholder: t('core.console.search'), 'aria-label': t('core.console.search') }) as HTMLInputElement;
  private results = h('div', { class: 'console-results', hidden: true });
  private crumbs = h('div', { class: 'console-crumbs' });
  private backBtn: HTMLButtonElement;
  private fwdBtn: HTMLButtonElement;
  private newBtn: HTMLButtonElement;
  private popBtn: HTMLButtonElement;
  private body = h('div', { class: 'console-body' });
  private pageEl = h('div', { class: 'console-page' });
  private detailEl = h('div', { class: 'console-detail' });
  private page: Slot | null = null;
  private details: Slot[] = [];
  private nodeId: string | null = null;
  private history: string[] = [];
  private pos = -1;
  private mode: Mode;
  private prefKey: string;
  private saved: Saved;
  /** uzly, jejichž potomci se načetli (po obnovení stromu se načtou znovu) */
  private loaded = new Set<string>();
  /** poslední klik / klávesa s Shiftem → otevřít do nového okna */
  private shift = false;
  private searchSeq = 0;
  private refreshTree = debounce(() => void this.reloadTree(), 400);
  /** nové ovládání (vzor Kontakty): lišta stránky v záhlaví okna, pod ním adresní řádek jako v Souborech */
  private barMode = useBar();
  private address: IwAddressBar | null = null;
  private toolsBtn: HTMLButtonElement | null = null;
  private saveState = debounce(() => void savePref(this.prefKey, this.saved), 800);
  private serverSearch = debounce(() => void this.runSearch(), 250);

  constructor(
    private desc: ConsoleDescriptor,
    private win: IwWindow,
    private wm: WindowManager,
    private host: ViewHost,
  ) {
    this.mode = wm.currentMode();
    this.prefKey = `console.${desc.app}.${desc.window}`;
    const btn = (ic: string, title: string, onClick: () => void): HTMLButtonElement => h('button', { type: 'button', class: 'btn ghost sm', title, 'aria-label': title, onClick }, icon(ic)) as HTMLButtonElement;
    const treeBtn = btn('sidebar', t('core.console.tree'), () => this.toggleTree());
    this.backBtn = btn('arrow-left', t('core.console.back'), () => this.back());
    this.fwdBtn = btn('arrow-right', t('core.console.forward'), () => void this.go(1));
    const refreshBtn = btn('refresh', t('core.cmd.refresh'), () => this.refresh());
    this.popBtn = btn('shortcut', t('core.console.new_window'), () => this.popOut());
    this.newBtn = h('button', { type: 'button', class: 'btn sm console-new', onClick: () => this.newMenu() }, icon('plus'), h('span', null, t('core.console.new')), icon('chevron-down')) as HTMLButtonElement;
    this.newBtn.hidden = !desc.commands.some((c) => c.kind === 'open');
    const collapseBtn = btn('chevron-up', t('core.console.collapse'), () => this.tree.collapseAll(this.nodeId ? this.ancestors(this.nodeId) : []));
    let head: HTMLElement;
    if (this.barMode) {
      // ☰ (strom) je v záhlaví okna; adresní řádek: ← → ↑ · cesta · ⋯ (obnovit, nové okno, založit z konzole)
      const address = new IwAddressBar();
      address.classList.add('console-address');
      this.address = address;
      this.toolsBtn = h('button', { type: 'button', class: 'btn ghost sm', title: t('core.ui.more'), 'aria-label': t('core.ui.more'), onClick: () => this.toolsMenu() }, icon('more')) as HTMLButtonElement;
      head = address;
      queueMicrotask(() => { address.extra().append(this.toolsBtn as HTMLButtonElement); this.updateNav(); });
    } else {
      head = h('div', { class: 'console-head' }, treeBtn, this.backBtn, this.fwdBtn, this.crumbs, h('div', { class: 'console-tools' }, refreshBtn, this.popBtn, this.newBtn));
    }
    this.split.first.append(h('div', { class: 'console-nav' }, h('div', { class: 'console-search-row' }, this.search, collapseBtn), this.results, this.tree));
    this.split.second.append(head, this.body);
    this.split.second.addEventListener('pointerdown', this.closeOverlayTree, true);
    this.split.classList.add('console-split');
    this.body.append(this.pageEl);
    this.el = h('div', { class: 'view console-view' }, this.split);

    const prefs = (window as unknown as { iwanPrefs?: Record<string, unknown> }).iwanPrefs ?? {};
    const raw = prefs[this.prefKey];
    this.saved = raw && typeof raw === 'object' ? { ...(raw as Saved) } : {};
    this.split.setWidth(Number(this.saved.tree ?? 250));
    this.split.defaultSize = 250;
    this.split.onResize = (px) => {
      this.saved.tree = Math.round(px);
      this.saveState();
    };

    this.tree.onSelect = (n) => void this.open(n.id);
    this.tree.onContext = (n, e) => this.nodeMenu(n, e);
    new ResizeObserver(() => this.checkNarrow()).observe(this.el);
    this.tree.onExpand = async (n) => {
      const node = this.find(n.id);
      if (node?.lazy) await this.load(node);
      return node ? this.nodes(node.children) : [];
    };
    this.tree.onToggle = (ids) => {
      this.saved.expanded = ids.slice(-200);
      this.saveState();
    };
    this.search.addEventListener('input', () => {
      this.applySearch();
      this.serverSearch();
    });
    this.search.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.search.value = '';
        this.applySearch();
        this.showResults([]);
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        this.results.querySelector<HTMLButtonElement>('.console-result')?.click();
      }
    });
    // Shift + dvojklik / Enter / klik na příkaz = otevřít do nového okna (zjistí se před openFrom)
    for (const ev of ['click', 'dblclick', 'keydown'] as const) this.el.addEventListener(ev, (e) => (this.shift = (e as MouseEvent | KeyboardEvent).shiftKey), true);

    this.setTree(desc.tree);
    this.setMode(this.mode);
    this.updateNav();
    void this.boot();
  }

  /** Obnoví rozbalené větve z předvoleb a otevře stránku: parametr node → poslední stránka → první stránka. */
  private async boot(): Promise<void> {
    const ids = (this.saved.expanded ?? []).filter((id) => typeof id === 'string');
    ids.sort((a, b) => a.split('/').length - b.split('/').length);
    const ok: string[] = [];
    for (const id of ids) {
      const n = await this.ensure(id).catch(() => null);
      if (!n) continue;
      if (n.lazy) await this.load(n).catch(() => undefined);
      ok.push(id);
    }
    this.applySearch();
    await this.tree.expandPath(ok);
    for (const id of [this.desc.start, this.saved.last, this.desc.first]) {
      if (id && (await this.open(id))) return;
    }
    this.pageEl.replaceChildren(emptyState(t('core.console.empty')));
  }

  // ---------------------------------------------------------------- strom

  private nodes(list: ConsoleNode[]): TreeNode[] {
    return list.map((n) => ({
      id: n.id, label: n.label, icon: n.icon ?? undefined, badge: n.badge ?? undefined, dot: n.dot ?? undefined, muted: n.muted, exclusive: n.exclusive,
      children: n.lazy ? null : this.nodes(n.children), data: n,
    }));
  }

  private setTree(tree: ConsoleNode[]): void {
    this.desc.tree = tree;
    this.applySearch();
  }

  /** Filtr načtené části stromu podle hledání (okamžitě); hledání na serveru běží zvlášť. */
  private applySearch(): void {
    const q = this.search.value.trim().toLowerCase();
    const filter = (list: ConsoleNode[]): ConsoleNode[] =>
      list.flatMap((n) => {
        if (!q || n.label.toLowerCase().includes(q)) return [n];
        const children = filter(n.children);
        return children.length ? [{ ...n, lazy: false, children }] : [];
      });
    const tree = filter(this.desc.tree);
    this.tree.setNodes(this.nodes(tree));
    const open: string[] = [];
    const walk = (list: ConsoleNode[]) => list.forEach((n) => { if (n.children.length && (q || !n.window)) open.push(n.id); walk(n.children); });
    if (q) walk(tree);
    else {
      // skupiny bez stránky (Systém, Pošta…) jsou rozbalené vždy
      const groups = (list: ConsoleNode[]) => list.forEach((n) => { if (!n.window && n.children.length) open.push(n.id); groups(n.children); });
      groups(tree);
    }
    if (this.nodeId) open.push(...this.ancestors(this.nodeId));
    void this.tree.expandPath(open);
    this.tree.select(this.nodeId);
  }

  private find(id: string, list: ConsoleNode[] = this.desc.tree): ConsoleNode | null {
    for (const n of list) {
      if (n.id === id) return n;
      const c = this.find(id, n.children);
      if (c) return c;
    }
    return null;
  }

  private path(id: string, list: ConsoleNode[] = this.desc.tree, acc: ConsoleNode[] = []): ConsoleNode[] | null {
    for (const n of list) {
      if (n.id === id) return [...acc, n];
      const p = this.path(id, n.children, [...acc, n]);
      if (p) return p;
    }
    return null;
  }

  private ancestors(id: string): string[] {
    return (this.path(id) ?? []).slice(0, -1).map((n) => n.id);
  }

  /** Načte potomky líně načítané větve. */
  private async load(node: ConsoleNode): Promise<void> {
    const res = await action<{ nodes: ConsoleNode[] }>(this.desc.app, this.desc.window, 'children', this.desc.params, { node: node.id });
    node.children = res.nodes;
    node.lazy = false;
    this.loaded.add(node.id);
  }

  /** Najde uzel, i dynamický (`rodič/klíč/…`): postupně načte větve na cestě k němu. */
  private async ensure(id: string): Promise<ConsoleNode | null> {
    for (let guard = 0; guard < 12; guard++) {
      const found = this.find(id);
      if (found) return found;
      let best: ConsoleNode | null = null;
      const walk = (list: ConsoleNode[]) => list.forEach((n) => {
        if (id.startsWith(n.id + '/') && (!best || n.id.length > best.id.length)) best = n;
        walk(n.children);
      });
      walk(this.desc.tree);
      const anc = best as ConsoleNode | null;
      if (!anc || !anc.lazy) return this.fetchOne(id);
      await this.load(anc);
    }
    return null;
  }

  /** Uzel za limitem větve (uživatel č. 900 z hledání): vyžádá si ho zvlášť a vloží k rodiči před „… dalších“. */
  private async fetchOne(id: string): Promise<ConsoleNode | null> {
    const parent = this.find(id.slice(0, id.lastIndexOf('/')));
    if (!parent) return null;
    const res = await action<{ node: ConsoleNode | null }>(this.desc.app, this.desc.window, 'node', this.desc.params, { node: id });
    if (!res.node) return null;
    const more = parent.children.findIndex((c) => c.id.endsWith('/__more'));
    parent.children.splice(more < 0 ? parent.children.length : more, 0, res.node);
    this.applySearch();
    return res.node;
  }

  /** Obnoví strom (po uložení záznamu): statické uzly z popisu, načtené větve znovu. */
  private async reloadTree(): Promise<void> {
    try {
      const d = await action<ConsoleDescriptor>(this.desc.app, this.desc.window, 'describe', this.desc.params);
      const ids = Array.from(this.loaded).sort((a, b) => a.split('/').length - b.split('/').length);
      this.desc.tree = d.tree;
      this.loaded.clear();
      for (const id of ids) {
        const n = this.find(id);
        if (n?.lazy) await this.load(n).catch(() => undefined);
      }
      // otevřený uzel za limitem větve (vložený z hledání) se doplní znovu
      if (this.nodeId && !this.find(this.nodeId)) await this.ensure(this.nodeId).catch(() => null);
      this.applySearch();
      this.renderCrumbs();
    } catch {
      /* strom zůstane, jak byl */
    }
  }

  // ---------------------------------------------------------------- hledání na serveru

  private async runSearch(): Promise<void> {
    const q = this.search.value.trim();
    const seq = ++this.searchSeq;
    if (q.length < 2) {
      this.showResults([]);
      return;
    }
    try {
      const res = await action<{ results: ConsoleSearchResult[] }>(this.desc.app, this.desc.window, 'search', this.desc.params, { q });
      if (seq === this.searchSeq) this.showResults(res.results);
    } catch {
      if (seq === this.searchSeq) this.showResults([]);
    }
  }

  private showResults(results: ConsoleSearchResult[]): void {
    const parts: HTMLElement[] = [];
    let group = '';
    for (const r of results) {
      if (r.group !== group) {
        group = r.group;
        parts.push(h('div', { class: 'console-results-group' }, group));
      }
      parts.push(h('button', {
        type: 'button', class: 'console-result', title: r.sub ? `${r.label} – ${r.sub}` : r.label,
        onClick: () => {
          this.search.value = '';
          this.showResults([]);
          this.applySearch();
          void this.open(r.id);
        },
      }, r.icon ? icon(r.icon) : null, h('span', { class: 'result-label' }, r.label), r.sub ? h('span', { class: 'result-sub' }, r.sub) : null));
    }
    this.results.replaceChildren(...parts);
    this.results.hidden = parts.length === 0;
  }

  // ---------------------------------------------------------------- navigace

  /** Otevře uzel stromu (stránku); skupina bez stránky jen rozbalí. false = uzel neexistuje / nejde otevřít. */
  async open(id: string, record = true): Promise<boolean> {
    const node = this.find(id) ?? (await this.ensure(id).catch(() => null));
    if (!node) return false;
    if (!node.window || !node.app) {
      return this.openGroup(node, record);
    }
    if (id === this.nodeId && this.page) {
      await this.closeDetails();
      return true;
    }
    if (!(await this.canLeave())) {
      this.tree.select(this.nodeId);
      return false;
    }
    this.destroyAll();
    this.nodeId = id;
    if (record) {
      this.history = this.history.slice(0, this.pos + 1);
      this.history.push(id);
      this.pos = this.history.length - 1;
    }
    await this.tree.expandPath(this.ancestors(id));
    if (node.lazy || node.children.length) void this.tree.expand(id); // otevřená položka ukáže i svůj obsah (zákazník → Uživatelé…)
    this.tree.select(id);
    this.tree.reveal();
    if (this.narrow) this.split.setFirstHidden(true);
    this.saved.last = id;
    this.saveState();
    this.page = await this.mount(this.pageEl, { app: node.app, window: node.window, params: asObject(node.params) }, node.label);
    this.renderCrumbs();
    this.layout();
    this.updateNav();
    this.win.onChange?.();
    return true;
  }

  /** Skupina bez vlastní stránky (Systém, Pošta…): přehled podřízených položek jako dlaždice se stavem. */
  private async openGroup(node: ConsoleNode, record: boolean): Promise<boolean> {
    if (!(await this.canLeave())) {
      this.tree.select(this.nodeId);
      return false;
    }
    this.destroyAll();
    this.nodeId = node.id;
    if (record) {
      this.history = this.history.slice(0, this.pos + 1);
      this.history.push(node.id);
      this.pos = this.history.length - 1;
    }
    await this.tree.expandPath([...this.ancestors(node.id), node.id]);
    this.tree.select(node.id);
    if (node.lazy) await this.load(node).catch(() => undefined);
    const tiles = node.children.map((c) => {
      const tile = h('button', { type: 'button', class: 'console-tile' + (c.muted ? ' muted' : '') },
        h('span', { class: 'console-tile-icon' }, icon(c.icon ?? (c.children.length || c.lazy ? 'folder' : 'app'))),
        h('span', { class: 'console-tile-label' }, c.label),
        c.dot ? h('span', { class: 'tree-dot ' + c.dot }) : null,
        c.badge ? h('span', { class: 'tree-badge' }, c.badge) : null);
      tile.addEventListener('click', () => void this.open(c.id));
      return tile;
    });
    this.pageEl.replaceChildren(h('div', { class: 'console-group' }, h('h2', { class: 'console-group-title' }, node.icon ? icon(node.icon) : null, node.label), h('div', { class: 'console-tiles' }, tiles)));
    this.page = null;
    this.layout();
    this.updateNav();
    this.renderCrumbs();
    this.popBtn.disabled = true;
    this.renderHeader();
    return true;
  }

  private async go(delta: number): Promise<void> {
    const next = this.pos + delta;
    const id = this.history[next];
    if (id === undefined) return;
    const before = this.pos;
    this.pos = next;
    await this.open(id, false);
    if (this.nodeId !== id) this.pos = before;
    this.updateNav();
  }

  /** Stránka (záznam) se sama zavřela – např. po smazání: zpět na nejbližší nadřazenou stránku stromu. */
  /** false = není kam se vrátit (kořenová stránka). */
  private closePage(): boolean {
    this.refreshTree();
    const up = (this.nodeId ? this.path(this.nodeId) ?? [] : []).slice(0, -1).reverse().find((n) => n.window);
    if (up) void this.open(up.id);
    return !!up;
  }

  /** Postaví pohled okna do prvku; vrátí slot (i při chybě – s chybovou hláškou a „Zkusit znovu“). */
  private async mount(el: HTMLElement, target: Target, fallbackTitle: string, isDetail = false): Promise<Slot> {
    const slot: Slot = { ...target, title: fallbackTitle, view: null, el, isDetail };
    const ld = loader();
    el.replaceChildren(ld.el);
    try {
      const d = await action<WindowDescriptor>(target.app, target.window, 'describe', target.params);
      if (d.kind === 'console') throw new ApiError('bad_request', 'core.console.nested', 400);
      slot.title = tk(d.title);
      slot.commands = d.commands;
      const host: ViewHost = isDetail
        ? { close: (r?: unknown) => { if (r !== undefined) slot.onResult?.(r); void this.popDetail(slot, true); }, result: (r: unknown) => { slot.onResult?.(r); this.refreshTree(); }, embedded: true }
        : { close: () => this.closePage(), result: () => this.refreshTree(), embedded: true };
      const view = createView(d, this.proxyWin(slot), this.wmFor(slot), host);
      slot.view = view;
      ld.remove();
      el.replaceChildren(view.el);
      view.setMode?.(this.mode);
    } catch (e) {
      const err = e as ApiError;
      ld.remove();
      el.replaceChildren(errorState(tk(err.message ?? 'core.err.internal', err.params ?? undefined), () => void this.remount(slot, isDetail), err.id));
    }
    this.renderCrumbs();
    return slot;
  }

  private async remount(slot: Slot, isDetail: boolean): Promise<void> {
    const fresh = await this.mount(slot.el, slot, slot.title, isDetail);
    if (!isDetail) this.page = fresh;
    else this.details = this.details.map((d) => (d === slot ? fresh : d));
  }

  /** Okno pro vložený pohled: titulek jde do drobečků (titulek okna nastaví konzole), parametry se nepřepisují. */
  private proxyWin(slot: Slot): IwWindow {
    return new Proxy(this.win, {
      get: (target, prop) => {
        if (prop === 'setTitle') return (title: string) => { slot.title = title; this.renderCrumbs(); };
        // záhlaví pohledu (lišta příkazů) ukáže konzole v záhlaví okna, je-li pohled právě nahoře
        if (prop === 'setHeader') return (parts: { start?: Node[]; main?: Node[] } | null) => { slot.header = parts; if (this.top() === slot) this.renderHeader(); };
        if (prop === 'resetChrome') return () => { slot.header = null; if (this.top() === slot) this.renderHeader(); };
        // zavření okna z pohledu (← na telefonu, Zavřít) = zavřít detail / stránku, ne celou konzoli
        // na kořenové stránce (← na telefonu) zavře celé okno konzole
        if (prop === 'close') return async () => { if (slot.isDetail) return this.popDetail(slot); return this.closePage() || this.win.close(); };
        if (prop === 'params') return slot.params;
        const v = Reflect.get(target, prop, target) as unknown;
        return typeof v === 'function' ? (v as (...a: unknown[]) => unknown).bind(target) : v;
      },
      set: (target, prop, value) => {
        if (prop === 'params') {
          slot.params = value as Record<string, unknown>;
          return true;
        }
        return Reflect.set(target, prop, value, target);
      },
    });
  }

  /** Správce oken pro vložený pohled: otevření okna (záznam ze seznamu, „Nový“…) zůstane v konzoli. */
  private wmFor(origin: Slot | null): WindowManager {
    return new Proxy(this.wm, {
      get: (target, prop) => {
        if (prop === 'open') return (opts: OpenOptions) => this.openFrom(origin, opts);
        const v = Reflect.get(target, prop, target) as unknown;
        return typeof v === 'function' ? (v as (...a: unknown[]) => unknown).bind(target) : v;
      },
    });
  }

  /**
   * Otevření okna z vložené stránky: Shift = nové okno; má-li okno uzel ve stromu (zákazník, uživatel, Služby…),
   * vybere se uzel; jinak (nový záznam) detail přes celou šířku nad tím, odkud se otevíralo.
   */
  private async openFrom(origin: Slot | null, opts: OpenOptions): Promise<IwWindow | null> {
    const shift = this.shift;
    this.shift = false;
    const target: Target = { app: opts.app, window: opts.window, params: asObject(opts.params), onResult: opts.onResult };
    // stránka v režimu operátora (data jiného zákazníka): otevřený záznam zůstane u stejného zákazníka (i v novém okně)
    const op = origin?.params['_customer'];
    if (typeof op === 'string' && op !== '' && target.params['_customer'] === undefined) target.params = { ...target.params, _customer: op };
    if (opts.newInstance || shift) return this.wm.open({ ...opts, params: target.params, newInstance: true });
    const nodeId = await this.locate(target);
    if (nodeId && (await this.open(nodeId))) return this.win;
    const idx = origin?.isDetail ? this.details.indexOf(origin) : -1;
    const above = this.details[idx + 1];
    if (above && !(await this.popDetail(above))) return null;
    await this.pushDetail(target);
    return this.win;
  }

  /** Uzel stromu k oknu: nejdřív v načteném stromu, pak se zeptá serveru (dynamické uzly). */
  private async locate(target: Target): Promise<string | null> {
    const same = (a: Record<string, unknown>, b: Record<string, unknown>) => {
      const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
      for (const k of keys) if (String(a[k] ?? '') !== String(b[k] ?? '')) return false;
      return true;
    };
    let hit: string | null = null;
    const walk = (list: ConsoleNode[]) => list.forEach((n) => {
      if (!hit && n.app === target.app && n.window === target.window && !n.id.endsWith('/__more') && same(asObject(n.params), target.params)) hit = n.id;
      walk(n.children);
    });
    walk(this.desc.tree);
    if (hit) return hit;
    if (!Object.values(target.params).some((v) => v !== undefined && v !== null && v !== '')) return null;
    try {
      const res = await action<{ node: string | null }>(this.desc.app, this.desc.window, 'locate', this.desc.params, { app: target.app, window: target.window, params: target.params });
      return res.node;
    } catch {
      return null;
    }
  }

  private async pushDetail(target: Target): Promise<void> {
    const el = h('div', { class: 'console-detail-layer' });
    this.details.forEach((d) => (d.el.hidden = true));
    this.detailEl.append(el);
    const placeholder: Slot = { ...target, title: t('core.ui.loading'), view: null, el };
    this.details.push(placeholder);
    this.layout();
    const slot = await this.mount(el, target, t('core.ui.loading'), true);
    this.details = this.details.map((d) => (d === placeholder ? slot : d));
    this.layout();
    this.renderCrumbs();
    this.updateNav();
    slot.view?.focus?.();
  }

  /** Zavře detail (a vše nad ním); force = bez dotazu na neuložené změny (záznam se sám zavřel). */
  private async popDetail(slot: Slot, force = false): Promise<boolean> {
    const i = this.details.indexOf(slot);
    if (i < 0) return true;
    const above = this.details.slice(i);
    if (!force) for (const d of [...above].reverse()) if (d.view?.canClose && !(await d.view.canClose())) return false;
    for (const d of above) {
      d.view?.destroy?.();
      d.el.remove();
    }
    this.details = this.details.slice(0, i);
    const top = this.details[this.details.length - 1];
    if (top) top.el.hidden = false;
    this.win.classList.remove('dirty');
    this.layout();
    this.renderCrumbs();
    this.updateNav();
    this.page?.view?.refresh?.();
    this.refreshTree();
    return true;
  }

  private async closeDetails(): Promise<boolean> {
    const first = this.details[0];
    return first ? this.popDetail(first) : true;
  }

  private async canLeave(): Promise<boolean> {
    for (const s of [...this.details].reverse()) if (s.view?.canClose && !(await s.view.canClose())) return false;
    if (this.page?.view?.canClose && !(await this.page.view.canClose())) return false;
    return true;
  }

  private destroyAll(): void {
    for (const d of this.details) {
      d.view?.destroy?.();
      d.el.remove();
    }
    this.details = [];
    this.page?.view?.destroy?.();
    this.page = null;
    this.win.classList.remove('dirty');
  }

  /** Detail vždy přes celou šířku (stránka pod ním zůstane, zavřením se vrátí). */
  /** Pohled nahoře (poslední detail, jinak stránka uzlu). */
  private top(): Slot | null {
    return this.details[this.details.length - 1] ?? this.page;
  }

  /** Záhlaví okna (ovládání bar): ☰ strom + záhlaví horního pohledu (lišta, hledání, ⚙). */
  private renderHeader(): void {
    if (!this.barMode) return;
    const parts = this.top()?.header ?? null;
    const tree = headButton('menu', t('core.console.tree'), () => this.toggleTree(), 'head-menu');
    const desk = this.desktopHead;
    this.win.classList.toggle('crumb-title', desk); // titulek okna nahradí cesta (drobečky)
    if (this.address) this.address.hidden = desk;
    if (!desk) {
      for (const n of parts?.main ?? []) if (n instanceof IwCommandBar) n.setExtraMore({ label: tk(this.desc.title), items: () => this.toolsItems() });
      this.win.setHeader({ start: [tree, ...(parts?.start ?? [])], main: parts?.main ?? [] });
      return;
    }
    const main = [...(parts?.main ?? [])];
    const bar = main.find((n): n is IwCommandBar => n instanceof IwCommandBar);
    const extra = { label: tk(this.desc.title), items: () => this.toolsItems() };
    if (bar) bar.setExtraMore(extra);
    else main.push(h('span', { class: 'head-spacer' }), headButton('more', t('core.ui.more'), (b) => IwMenu.showAt(this.toolsItems(), b)));
    this.win.setHeader({ start: [tree, this.backBtn, this.fwdBtn, this.crumbs, ...(parts?.start ?? [])], main });
  }

  /** Zpět: zavřít detail, jinak předchozí stránka (← v záhlaví i v adresním řádku na telefonu). */
  private back(): void {
    const d = this.details[this.details.length - 1];
    if (d) void this.popDetail(d);
    else void this.go(-1);
  }

  /** Telefon: adresní řádek (← → ↑ · cesta · ⋯); počítač: ← → a cesta v záhlaví okna, příkazy konzole v ⋯ lišty. */
  private get desktopHead(): boolean {
    return this.barMode && this.mode !== 'mobile';
  }

  /** ⋯ v adresním řádku: obnovit, otevřít v novém okně, založení z konzole (Nový zákazník…). */
  private toolsMenu(): void {
    if (this.toolsBtn) IwMenu.showAt(this.toolsItems(), this.toolsBtn);
  }

  private toolsItems(): MenuItem[] {
    const own = this.desc.commands.filter((c) => c.kind === 'open' && c.opens).map((c): MenuItem => ({
      label: tk(c.label), icon: c.icon,
      onSelect: () => void this.closeDetails().then((ok) => { const o = c.opens!; if (ok) void this.pushDetail({ app: o.app ?? this.desc.app, window: o.window, params: {} }); }),
    }));
    return [
      { id: '__refresh', label: t('core.cmd.refresh'), icon: 'refresh', onSelect: () => this.refresh() },
      { label: t('core.console.new_window'), icon: 'shortcut', disabled: !this.top(), onSelect: () => this.popOut() },
      { label: t('core.console.collapse'), icon: 'chevron-up', onSelect: () => this.tree.collapseAll(this.nodeId ? this.ancestors(this.nodeId) : []) },
      ...(own.length ? [{ separator: true, label: '' }, ...own] : []),
    ];
  }

  private layout(): void {
    this.renderHeader();
    const has = this.details.length > 0;
    this.body.classList.toggle('has-detail', has);
    if (has && !this.detailEl.isConnected) this.body.append(this.detailEl);
    if (!has) this.detailEl.remove();
  }

  // ---------------------------------------------------------------- hlavička

  private renderCrumbs(): void {
    const node = this.nodeId ? this.path(this.nodeId) ?? [] : [];
    const parts: HTMLElement[] = node.map((n, i) => {
      const last = i === node.length - 1 && this.details.length === 0;
      return h('button', { type: 'button', class: 'crumb' + (last ? ' current' : ''), onClick: () => (n.window ? void this.open(n.id) : void this.tree.expand(n.id)) }, n.label);
    });
    this.details.forEach((d, i) => {
      const last = i === this.details.length - 1;
      parts.push(h('button', { type: 'button', class: 'crumb' + (last ? ' current' : ''), onClick: () => { const next = this.details[i + 1]; if (next) void this.popDetail(next); } }, d.title));
    });
    if (this.barMode) parts.unshift(h('button', { type: 'button', class: 'crumb crumb-root', onClick: () => { const first = this.desc.tree.find((n) => n.window); if (first) void this.open(first.id); } }, tk(this.desc.title)));
    this.crumbs.replaceChildren(...parts.flatMap((p, i) => (i ? [icon('chevron', 'icon crumb-sep'), p] : [p])));
    if (this.address) {
      const path = [
        ...node.map((n) => ({ id: 'n:' + n.id, name: n.label })),
        ...this.details.map((d, i) => ({ id: 'd:' + i, name: d.title })),
      ];
      this.address.setPath(path, (id) => {
        if (id.startsWith('d:')) { const next = this.details[Number(id.slice(2)) + 1]; if (next) void this.popDetail(next); return; }
        const n = this.find(id.slice(2));
        if (n?.window) void this.open(n.id);
        else if (n) void this.tree.expand(n.id);
      });
    }
    const top = this.details[this.details.length - 1] ?? this.page;
    const nodeLabel = node[node.length - 1]?.label ?? null; // „Limity (odesílání a příjem)“, ne „Nastavení serveru“
    const pageTitle = this.details.length ? top?.title ?? null : nodeLabel ?? top?.title ?? null;
    this.win.setTitle(pageTitle ? (this.mode === 'mobile' ? pageTitle : `${tk(this.desc.title)} – ${pageTitle}`) : tk(this.desc.title), this.desc.icon);
    this.popBtn.disabled = !top;
  }

  private updateNav(): void {
    this.backBtn.disabled = this.pos <= 0 && this.details.length === 0;
    this.fwdBtn.disabled = this.pos >= this.history.length - 1;
    const up = this.details.length ? null : (this.nodeId ? this.path(this.nodeId) ?? [] : []).slice(0, -1).reverse().find((n) => n.window);
    this.address?.setNav({
      canBack: this.pos > 0 || this.details.length > 0, canForward: this.pos < this.history.length - 1, canUp: !!up || this.details.length > 0,
      onBack: () => { const d = this.details[this.details.length - 1]; if (d) void this.popDetail(d); else void this.go(-1); },
      onForward: () => void this.go(1),
      onUp: () => { const d = this.details[0]; if (d) void this.popDetail(d); else if (up) void this.open(up.id); },
    });
  }

  /** „Nový ▾“: nejdřív Nový… zobrazené stránky (Nová schránka, Nový uživatel…), pod čarou založení z konzole. */
  private newMenu(): void {
    const item = (c: WindowDescriptor['commands'][number], fallbackApp: string, own: Record<string, unknown>) => ({
      label: tk(c.label),
      icon: c.icon,
      onSelect: () => {
        const o = c.opens!;
        const params: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(o.params)) {
          if (v.startsWith('=')) params[k] = v.slice(1);
          else if (v.startsWith('$')) params[k] = own[v.slice(1)];
        }
        void this.closeDetails().then((ok) => { if (ok) void this.pushDetail({ app: o.app ?? fallbackApp, window: o.window, params }); });
      },
    });
    const isNew = (c: WindowDescriptor['commands'][number]) => c.kind === 'open' && !!c.opens && c.group === 'core.grp.new' && !c.opens.row;
    const page = this.page && this.page.commands ? this.page.commands.filter(isNew).map((c) => item(c, this.page!.app, this.page!.params)) : [];
    const own = this.desc.commands.filter((c) => c.kind === 'open' && c.opens).map((c) => item(c, this.desc.app, {}));
    const items = [...page, ...(page.length && own.length ? [{ separator: true, label: '' }] : []), ...own];
    if (items.length) IwMenu.showAt(items, this.newBtn);
  }

  /** Pravé tlačítko na uzlu stromu: otevřít, otevřít v novém okně, rozbalit / sbalit. */
  private nodeMenu(n: TreeNode, e: MouseEvent): void {
    const node = this.find(n.id);
    if (!node) return;
    const items = [
      { label: t('core.cmd.open'), icon: 'edit', onSelect: () => void this.open(node.id) },
      ...(node.window && node.app ? [{ label: t('core.console.new_window'), icon: 'shortcut', onSelect: () => void this.wm.open({ app: node.app!, window: node.window!, params: asObject(node.params), newInstance: true }) }] : []),
      ...(node.children.length || node.lazy ? [{ label: t('core.console.expand'), icon: 'chevron-down', onSelect: () => void this.tree.expand(node.id) }] : []),
    ];
    IwMenu.show(items, e.clientX, e.clientY);
  }

  /** Aktuální stránku nebo detail otevřít jako samostatné okno (porovnání dvou zákazníků vedle sebe). */
  private popOut(): void {
    const top = this.details[this.details.length - 1] ?? this.page;
    if (top) void this.wm.open({ app: top.app, window: top.window, params: top.params, newInstance: true });
  }

  private toggleTree(): void {
    this.split.setFirstHidden(!this.split.classList.contains('first-hidden'));
  }

  /** Vysunutý strom přes stránku: klepnutí na ztmavenou stránku ho zavře. */
  private closeOverlayTree = (e: Event): void => {
    if (!this.split.classList.contains('overlay') || this.split.classList.contains('first-hidden')) return;
    e.preventDefault();
    e.stopPropagation();
    this.split.setFirstHidden(true);
  };

  // ---------------------------------------------------------------- WindowView

  refresh(): void {
    this.refreshTree();
    this.page?.view?.refresh?.();
    for (const d of this.details) d.view?.refresh?.();
  }

  /** Úzké okno (pod NARROW px): strom jako vysouvací panel přes stránku. */
  private narrow = false;
  private checkNarrow(): void {
    const w = this.el.getBoundingClientRect().width;
    const n = this.mode === 'mobile' || (w > 0 && w < NARROW);
    if (n === this.narrow || w === 0) return;
    this.narrow = n;
    this.split.setOverlay(n);
    this.split.setFirstHidden(n);
  }

  setMode(mode: Mode): void {
    this.mode = mode;
    this.narrow = mode === 'mobile';
    this.split.setOverlay(this.narrow);
    if (mode !== 'mobile') this.split.setFirstHidden(false);
    this.checkNarrow();
    this.page?.view?.setMode?.(mode);
    for (const d of this.details) d.view?.setMode?.(mode);
    this.layout();
  }

  getState(): unknown {
    return { node: this.nodeId };
  }

  setState(state: unknown): void {
    const node = (state as { node?: unknown } | null)?.node;
    if (typeof node === 'string' && node !== this.nodeId) void this.open(node);
  }

  async canClose(): Promise<boolean> {
    return this.canLeave();
  }

  destroy(): void {
    this.destroyAll();
  }

  focus(): void {
    (this.details[this.details.length - 1]?.view ?? this.page?.view)?.focus?.();
  }

  /** Klávesové zkratky jdou do detailu, má-li fokus, jinak do stránky. */
  shortcut(sc: string): boolean {
    const top = this.details[this.details.length - 1];
    const target = top && document.activeElement && top.el.contains(document.activeElement) ? top.view : (this.page?.view ?? null);
    const v = target as (WindowView & { shortcut?: (s: string) => boolean }) | null;
    return v?.shortcut?.(sc) ?? false;
  }
}
